import { useContext, useState } from "react";

import {
    FileDownloadIcon,
    ReplayIcon,
    TaskInfoOutlineIcon
} from "@dotnet/telematics-design-system";

import { useTaskTypes } from "components/Actions/dialogs/Devices/CreateTask/hooks";
import TableContext from "context/table/tableContext";
import {
    downloadFile,
    ENDPOINTS,
    isFailedOrCompletedWithErrorsTask,
    RenderConditionOptions,
    TABLE_NAMES,
    useApi,
    usePermissions
} from "shared";

import { useDialog } from "../../ActionBar/hooks";
import { DEVICE_ACTIONS } from "../../types";
import { TITLES } from "../constants";

const useTaskInlineActions = () => {
    const { tableName } = useContext(TableContext);

    const { getData, handleResponse, postData } = useApi();
    const { isRetriableTaskType, isTaskFileDeleted } = useTaskTypes();
    const { isOpenDialog, openDialog, closeDialog } = useDialog();
    const { PERMISSIONS, getNoPermissionTooltipTitle } = usePermissions();

    const [taskData, setTaskData] = useState<any>(null);

    const handleSubmitRetry = async (id: number, callbackFn: () => void) => {
        try {
            await postData(`${ENDPOINTS.Tasks}/${id}/${TITLES.Retry}`);
        } catch (error) {
            handleResponse(error);
        }

        callbackFn();
    };

    const retryAction = {
        title: TITLES.Retry,
        hasLoader: true,
        icon: ReplayIcon,
        getDisabledText: () =>
            getNoPermissionTooltipTitle(PERMISSIONS.TaskRetry),
        clickAction: (data: any, callbackFn: () => void) =>
            handleSubmitRetry(data.id, callbackFn),
        renderConditions: ({ data }: RenderConditionOptions) => {
            const { type, status_id, file_id, file } = data;

            return (
                isRetriableTaskType(type) &&
                isFailedOrCompletedWithErrorsTask(status_id) &&
                !isTaskFileDeleted(file_id, file)
            );
        }
    };

    const inlineActions = [
        ...(tableName === TABLE_NAMES.Devices ? [retryAction] : []),
        {
            title: TITLES.DownloadFile,
            hasLoader: true,
            icon: FileDownloadIcon,
            getDisabledText: () =>
                getNoPermissionTooltipTitle(PERMISSIONS.FileDownload),
            clickAction: (data: any, removeLoading: () => void) =>
                downloadFile({
                    id: data.file.id,
                    getData,
                    handleResponse,
                    removeLoading
                }),
            renderConditions: ({
                data,
                outerDetailsData
            }: RenderConditionOptions) => {
                const hasNoBatchFile = outerDetailsData
                    ? !outerDetailsData.file
                    : true;

                return hasNoBatchFile && Boolean(data?.file?.is_downloadable);
            }
        },
        {
            title: TITLES.TaskDetails,
            icon: TaskInfoOutlineIcon,
            clickAction: (data: any) => {
                setTaskData(data);
                openDialog(DEVICE_ACTIONS.TaskDetails);
            }
        }
    ];

    return {
        inlineActions,
        deviceGroupActions: [retryAction],
        taskData,
        isOpen: isOpenDialog[DEVICE_ACTIONS.TaskDetails],
        closeDialog,
        setTaskData
    };
};

export { useTaskInlineActions };
